/**
 * Returns the top/left position of an element on the page
 * Because getBoundingClientRect() returns invalid value on iOS after an OSK (on-screen keyboard),
 * we calculate the offset the old fashed way
 * We could compensate with visualViewport.offsetTop, but this only works for iOS v13+
 * iOS problem: https://openradar.appspot.com/radar?id=6668472289329152
 */
export function getOffset(element: HTMLElement | null) {
	let offsetLeft = 0;
	let offsetTop = 0;

	while (element) {
		offsetLeft += element.offsetLeft;
		offsetTop += element.offsetTop;
		element = element.offsetParent as HTMLElement;
	}
	return eXpress.math.point(offsetLeft, offsetTop);
}

/**
 * Similar to `DOM.scrollIntoView({ block: "nearest"})` + CSS with scroll-top/bottom-margin
 *
 * `DOM.scrollIntoView` doesn't work when 2 simultanious scrolls need to happen
 * Example: Bottom menu: scroll font button  into view + TextFontFamilyPanel: scroll selecteded font into view
 * https://stackoverflow.com/questions/49318497/google-chrome-simultaneously-smooth-scrollintoview-with-more-elements-doesn
 * I checked for an existing npm package (tested with compute-scroll-into-view) but I couldn't specify an extra margin.
 *
 *
 * Another defference with the native implementation
 * The native implementation does not scroll when element is in view and less than margin away from the side
 */
export function scrollIntoView(
	el: Element | null | undefined,
	{
		animate = true,
		margins,
		container,
		behavior,
	}: {
		behavior?: ScrollBehavior; // only used when animate is false, because with animate we use 'smooth' and with no animation we use 'auto'
		/* @deprecated, use behavior */
		animate?: boolean;
		margins?: eXpress.math.IMargins;
		container?: HTMLElement | null;
	},
) {
	if (!el) return;

	// Currently, parent container, TODO: find first offset element with overflow?
	const parentEl = container ?? el.parentElement;
	if (!parentEl || !parentEl.scrollTo) return; // In React jest tests, the parentEl has no .scrollTo function
	const parentRect = eXpress.math.rect(parentEl.getBoundingClientRect()).quant(1);

	if (behavior) {
		if (behavior === "smooth" || behavior === "auto") animate = true;
		else if (behavior === "instant") animate = false;
	}

	const elRect = el.getBoundingClientRect();

	// Horizontal
	let left = Math.ceil(elRect.left - (margins?.left ?? 0) - parentRect.left);
	let right = Math.floor(elRect.right + (margins?.right ?? 0) - parentRect.right);
	let scrollLeftExtra = 0;

	// If the item doesn't fit horizontally, we scroll to center
	if (left - right < 0) {
		const avg = (left - right) / -2;
		left += avg;
		right -= avg;
	}

	// left is before view, scroll left
	if (left < 0) {
		// Left also not visible
		if (-right > parentRect.width) {
			// left + right are before view
			scrollLeftExtra = right;
		} else {
			scrollLeftExtra = left;
		}
	}

	// right is after view, scroll right
	else if (right > 0) {
		if (left > parentRect.width) {
			// left + right are after view
			scrollLeftExtra = left;
		} else {
			scrollLeftExtra = right;
		}
	}

	// Vertical
	/** extra scroll value to make element appear at the top, negative means item need scroll needed to get it in view */
	let top = Math.ceil(elRect.top - (margins?.top ?? 0) - parentRect.top);
	/** extra scroll value to make element appear at the bottom, positive means item needs to scroll to get it in view */
	let bottom = Math.floor(elRect.bottom + (margins?.bottom ?? 0) - parentRect.bottom);
	let scrollTopExtra = 0;

	// If the item doesn't vertically, we scroll to center
	if (top - bottom < 0) {
		const avg = (top - bottom) / -2;
		top += avg;
		bottom -= avg;
	}

	// top is above view, scroll up
	if (top < 0) {
		// Top also not visible
		if (-bottom > parentRect.height) {
			// top + bottom are above view
			scrollTopExtra = bottom;
		} else {
			scrollTopExtra = top;
		}
	}

	// bottom is below view, scroll down
	else if (bottom > 0) {
		if (top > parentRect.height) {
			// top + bottom are below view
			scrollTopExtra = top;
		} else {
			scrollTopExtra = bottom;
		}
	}

	parentEl.scrollTo({
		// with scrollTo() we can animated 2 items together
		left: parentEl.scrollLeft + scrollLeftExtra,
		top: parentEl.scrollTop + scrollTopExtra,
		behavior: animate ? "smooth" : "auto",
	});
}

export function getCenterOfElement(dragImageEl: HTMLImageElement) {
	const sourceRect = dragImageEl.getBoundingClientRect();
	const fromX = sourceRect.left + sourceRect.width / 2; // use left from getBoundingClientRect because tht takes the scroll position into account
	const fromY = sourceRect.top + sourceRect.height / 2;
	return { left: fromX, top: fromY };
}
